import { JournalArticleTasticProps } from '@srcDS/js/tastic/pages/journal/journalArticle/tastic';
import { ConfigurationSchema } from 'frontastic-common';
import { Helmet } from 'react-helmet';
import { useSelector } from 'react-redux';
import { useMountedState } from 'react-use';
import { formatUrl } from '../../utils';

interface JournalCategorySchemaProps {
  title: string;
  description: string;
  keywords: string;
  url: string;
  image: {
    url: string;
    title: string;
    type: string;
    contentType: string;
    width: number;
    height: number;
  };
  articles?: any[];
  placement: string;
  article: JournalArticleTasticProps['data']['blogArticleData']['article'];
  showCanonicalLink: boolean;
}

const JournalCategorySchema = ({
  title: metaTitle,
  description,
  keywords,
  url: metaUrl,
  image,
  articles = [],
  placement = 'Category',
  article = {},
  showCanonicalLink,
}: JournalCategorySchemaProps) => {
  const isMounted = useMountedState();
  const context = useSelector((state: any) => state.app.context);

  const truncateDescription = (description, maxLength) => {
    let truncatedDescription = description;
    if ((description && description.length) > maxLength) {
      truncatedDescription = description.substring(0, maxLength - 3) + '...';
    }
    return truncatedDescription;
  };

  const projectSchema = new ConfigurationSchema(context.projectConfigurationSchema, context.projectConfiguration);

  // TITLE
  const pageTitleSuffixSchema = projectSchema.get('frontasticPageTitleSuffix');
  const pageTitleSuffix =
    pageTitleSuffixSchema?.[context.locale] || pageTitleSuffixSchema?.['en_AU@AUD'] || pageTitleSuffixSchema;

  const title = `${metaTitle}${pageTitleSuffix}`;
  // URL
  const storeUrl = context.projectConfiguration['EXTENSION_PUBLIC_URL'];
  const url = metaUrl ? `${storeUrl}${metaUrl}` : typeof window !== 'undefined' ? window.location.href : '';

  // SCHEMA
  const categorySchema = {
    '@context': 'http://schema.org',
    '@type': placement === 'BlogHome' ? 'Blog' : placement === 'Category' ? 'ItemList' : 'ItemList',
    url,
    name: title,
    description: truncateDescription(description, 200),
    itemListElement: articles?.map((article, index) => ({
      '@type': 'ListItem',
      position: index + 1,
      url: `${storeUrl}${formatUrl(article?.url) ?? ''}`,
    })),
  };

  const articleSchema = {
    '@context': 'http://schema.org',
    '@type': 'Article',
    url: url,
    mainEntityOfPage: {
      '@type': 'WebPage',
      '@id': url,
    },
    headline: article?.title,
    image: {
      '@type': 'ImageObject',
      url: article?.thumbnail?.desktop?.url,
    },
    datePublished: article?.publishDate,
    description: truncateDescription(article?.shortDescription, 200),
    articleBody: truncateDescription(article?.shortDescription, 200),
    author: {
      '@type': 'Person',
      name: `${article?.author?.firstName} ${article?.author?.lastName}`,
    },
  };

  const schema = placement === 'Article' ? articleSchema : categorySchema;

  return (
    <>
      <Helmet>
        <title>{title}</title>
        <meta name="title" content={title} />
        <meta name="description" content={description} />
        <meta content="text/html; charset=UTF-8" name="Content-Type" />
        <meta property="og:url" content={url} />
        {image && <meta property="og:image" content={image as any} />}
        {image && <meta property="og:image:width" content={image?.width?.toString() ?? '1200'} />}
        {image && <meta property="og:image:height" content={image?.height?.toString() ?? '628'} />}
        <meta name="twitter:card" content="summary_large_image" />
        {image && <meta name="twitter:image" content={image as any} />}
        {image && <meta property="twitter:image:width" content={image?.width?.toString() ?? '1200'} />}
        {image && <meta property="twitter:image:height" content={image?.height?.toString() ?? '628'} />}
        <meta name="twitter:title" content={title} />
        <meta name="twitter:description" content={description} />
        <meta property="og:type" content="blog" />
        <meta property="og:site_name" content={projectSchema.get('schemaOrgName')} />
        <meta property="og:locale" content="en-AU" />
        <meta property="og:title" content={title} />
        <meta property="og:description" content={description} />
        <meta property="og:country-name" content="au" />
        {showCanonicalLink && <link rel="canonical" href={url} />}
        {keywords && <meta name="keywords" content={keywords} />}
      </Helmet>
      {isMounted() && (
        <script
          type="application/ld+json"
          dangerouslySetInnerHTML={{
            __html: JSON.stringify(schema),
          }}
        />
      )}
    </>
  );
};

export default JournalCategorySchema;
